import { useEffect, useRef, useState, type ElementType, type ReactNode } from 'react';
import { cx } from '@/lib/format';

/**
 * Reveals content on scroll using a plain IntersectionObserver and a CSS
 * transition — deliberately not a motion library.
 *
 * The rule that matters: an unconditional timer marks the element visible
 * shortly after mount no matter what the observer decides. A scroll-triggered
 * animation that fails leaves content stuck at opacity 0, which on a
 * client-side route change means a blank page until the visitor reloads.
 * Visibility must never depend on the animation succeeding.
 */
function useReveal(): [React.RefObject<HTMLElement>, boolean] {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;

    if (!node || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );

    observer.observe(node);

    const failsafe = window.setTimeout(() => {
      setShown(true);
      observer.disconnect();
    }, 800);

    return () => {
      observer.disconnect();
      window.clearTimeout(failsafe);
    };
  }, []);

  return [ref, shown];
}

interface RevealProps {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: ElementType;
}

export function Reveal({ children, delay = 0, className, as: Tag = 'div' }: RevealProps) {
  const [ref, shown] = useReveal();

  return (
    <Tag
      ref={ref}
      className={cx('reveal', shown && 'reveal-in', className)}
      style={delay ? { transitionDelay: `${delay}s` } : undefined}
    >
      {children}
    </Tag>
  );
}

interface StaggerProps {
  children: ReactNode;
  className?: string;
  as?: ElementType;
}

/** Children reveal in sequence; the delay comes from `--i` set per item. */
export function Stagger({ children, className, as: Tag = 'div' }: StaggerProps) {
  const [ref, shown] = useReveal();

  return (
    <Tag ref={ref} className={cx('stagger', shown && 'stagger-in', className)}>
      {children}
    </Tag>
  );
}

export function StaggerItem({
  children,
  className,
  index = 0,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  index?: number;
  as?: ElementType;
}) {
  return (
    <Tag className={cx('stagger-item', className)} style={{ '--i': index } as React.CSSProperties}>
      {children}
    </Tag>
  );
}
